import type { JSX, ReactNode, KeyboardEvent, MouseEvent } from 'react';
import { cn } from '@/lib/utils';
import { Icon, type IconName } from '@/components/ui/icon';
import { Button, type ButtonProps } from '@/components/ui/button';
import { statusToneTextClass, type StatusTone } from '@/lib/statusTone';

interface EntityActionButton {
  label: string;
  onClick: () => void;
  icon?: IconName;
  variant?: ButtonProps['variant'];
}

interface EntityActionCardProps {
  title: string;
  icon?: IconName;
  /** Location subtitle (e.g., "Milano (MI)") */
  subtitle?: string;
  /** Label for the primary value row */
  primaryValueLabel: string;
  /** The primary value to display (e.g., date) */
  primaryValue: ReactNode;
  /** Status badge next to primary value (e.g., "Tra 2 mesi") */
  primaryValueStatus?: {
    label: string;
    tone: StatusTone;
  };
  /** Chips row for badges (Impianti, NC, Bozza) */
  chips?: ReactNode;
  /** Primary action (e.g., "Pianifica") - shown as a distinct action in the toolbar */
  primaryAction?: EntityActionButton;
  /** Secondary action (e.g., "Resume Draft") - shown as a distinct action in the toolbar */
  secondaryAction?: EntityActionButton;
  /** Navigate handler for clicking the card */
  onNavigate?: () => void;
  size?: 'default' | 'large';
  className?: string;
}

export function EntityActionCard({
  title,
  icon,
  subtitle,
  primaryValueLabel,
  primaryValue,
  primaryValueStatus,
  chips,
  primaryAction,
  secondaryAction,
  onNavigate,
  size = 'large',
  className,
}: EntityActionCardProps): JSX.Element {
  const IconComponent = icon ? (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-blue-50 text-brand-blue-600 transition-colors group-hover:bg-brand-blue-100 group-hover:text-brand-blue-700">
      <Icon name={icon} className="h-5 w-5" />
    </div>
  ) : null;

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onNavigate) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onNavigate();
    }
  };

  const stopPropagation = (handler: () => void) => (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handler();
  };

  const sizeConfig = {
    default: {
      wrapper: 'p-5',
      title: 'text-base',
    },
    large: {
      wrapper: 'p-5',
      title: 'text-lg',
    },
  }[size];

  const statusToneClass = primaryValueStatus?.tone
    ? statusToneTextClass(primaryValueStatus.tone)
    : undefined;

  const hasActions = primaryAction || secondaryAction;

  return (
    <div
      role={onNavigate ? 'button' : undefined}
      tabIndex={onNavigate ? 0 : undefined}
      onClick={onNavigate}
      onKeyDown={handleCardKeyDown}
      className={cn(
        'group relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-[var(--border-default)] bg-surface text-left transition-all duration-200',
        'shadow-sm hover:-translate-y-0.5 hover:shadow-md hover:border-brand-blue-300',
        onNavigate &&
          'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500/30 focus-visible:ring-offset-2',
        className
      )}
    >
      {/* Blue Navigation Indicator (Clean Strip) */}
      <div className="absolute bottom-0 left-0 top-0 w-1 bg-brand-blue-500 transition-all group-hover:w-1.5" />

      <div className={cn('flex flex-1 flex-col gap-3', sizeConfig.wrapper, 'pl-5 py-4 pr-4')}>
        {/* Header: Icon + Title + Subtitle */}
        <div className="flex items-start gap-3">
          {IconComponent}
          <div className="min-w-0 flex-1 flex flex-col justify-center">
            <p
              className={cn(
                'truncate font-bold leading-tight text-text-primary transition-colors group-hover:text-brand-blue-700',
                sizeConfig.title
              )}
            >
              {title}
            </p>
            {subtitle ? (
              <p className="truncate text-sm text-text-secondary mt-0.5">{subtitle}</p>
            ) : null}
          </div>
        </div>

        {/* Separator */}
        <div className="h-px w-full bg-border-subtle/30" />

        {/* Primary value row with status */}
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary opacity-80">
            {primaryValueLabel}
          </p>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-base font-mono font-medium text-text-primary">
              {primaryValue}
            </span>
            {primaryValueStatus ? (
              <span className={cn('shrink-0 text-sm font-medium', statusToneClass)}>
                {primaryValueStatus.label}
              </span>
            ) : null}
          </div>
        </div>

        {/* Chips row */}
        {chips ? (
          <div className="flex flex-wrap gap-2 mt-auto">{chips}</div>
        ) : (
          <div className="mt-auto" />
        )}

        {/* Toolbar Actions (Secondary) */}
        {hasActions ? (
          <div className="mt-2 flex items-center justify-end gap-2 border-t border-border-subtle/40 pt-3">
            {secondaryAction ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={stopPropagation(secondaryAction.onClick)}
                className="h-8 px-2 text-xs text-text-secondary hover:bg-neutral-100 hover:text-brand-blue-600"
              >
                {secondaryAction.icon && (
                  <Icon name={secondaryAction.icon} className="mr-1.5 h-3.5 w-3.5" />
                )}
                {secondaryAction.label}
              </Button>
            ) : null}
            {primaryAction ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={stopPropagation(primaryAction.onClick)}
                className="h-8 px-2 text-xs text-brand-blue-600 hover:bg-brand-blue-50 hover:text-brand-blue-700"
              >
                {primaryAction.icon && (
                  <Icon name={primaryAction.icon} className="mr-1.5 h-3.5 w-3.5" />
                )}
                {primaryAction.label}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
